import _ from 'lodash'
import { imgixHost } from 'src/config'
import Discount from 'src/models/discount/Discount'
import Category from 'src/models/item/Category'
import Model, { encoders, ID, MODEL_LEVEL } from 'src/models/model'
import Librariable from 'src/models/model/concerns/Librariable'
import ItemPackageItem from 'src/models/package/ItemPackageItem'

export function getTicketTypesFromItemPackage (itemPackage) {
  return _.compact(
    _.map(
      itemPackage.itemPackageItems.data,
      (itemPackageItem) => itemPackageItem.ticketType
    )
  )
}

export default class ItemPackage extends Model {
  static name = 'ItemPackage'

  static isConfiguration = true
  static duplicatable = true
  static paginate = true

  static versioned = true

  static level = MODEL_LEVEL.SITE

  static employeeRequiredPermissionCategories = [Model.permissionCategories.Concession]

  static define () {
    this.concerns = [Librariable]

    this.fields = {
      id: {
        type: ID,
        sortable: true,
        required: true
      },
      name: {
        type: String,
        sortable: true,
        default: null,
        translatable: true
      },
      color: {
        type: String
      },
      image: {
        type: String,
        default: null
      },
      itemPackageItems: {
        type: [ItemPackageItem],
        allowDestroy: true,
        reference: true,
        acceptsNestedAttributes: true
      },
      discounts: {
        type: [Discount],
        default: () => { return [] },
        encode (data) {
          data.discountIds = _.map(this.discounts.data, 'id')
        },
        reference: true
      },
      category: {
        type: Category,
        default: null,
        encode: encoders.hasOne,
        reference: true
      },
      availableOnline: {
        type: Boolean,
        default: false
      },
      displayOrder: {
        type: Number,
        default: 0
      },
      applyAutomatically: {
        type: Boolean,
        default: false
      }
    }

    this.computed = {
      backgroundForImgix () {
        if (this.color) {
          return this.color.slice(1)
        } else {
          return 'FFFFFF'
        }
      },
      imageUrl () {
        if (!this.image) return ''

        return `${imgixHost}/${this.image}?w=190&h=118&fit=crop&fill=solid&fill-color=${this.backgroundForImgix}&bg=${this.backgroundForImgix}&auto=format`
      },
      itemOnlyPackage () {
        return _.find(this.itemPackageItems.data, 'ticketType') === undefined
      }
    }
  }

  get ticketTypeDescriptions () {
    const allTicketTypes = _.compact(_.map(this.itemPackageItems.data, 'ticketType'))
    const filteredList = _.uniq(allTicketTypes)
    const ticketTypeDescriptions = _.map(filteredList, (ticketType) => {
      return { id: _.find(this.itemPackageItems.data, item => item.ticketType?.id === ticketType.id)?.id, description: `${ticketType.description} x${_.filter(allTicketTypes, ticketType).length}` }
    })

    return ticketTypeDescriptions
  }

  get itemVariationDescription () {
    const allItemVariations = _.compact(_.map(this.itemPackageItems.data, 'itemVariation'))
    const filteredList = _.uniq(allItemVariations)
    const itemVariationDescriptions = _.map(filteredList, (itemVariation) => {
      return {
        id: _.find(this.itemPackageItems.data, item => item.itemVariation?.id === itemVariation.id)?.id,
        name: `${itemVariation.nameWithItem} x${_.filter(allItemVariations, itemVariation).length}`,
        description: `${itemVariation.nameWithItem} x${_.filter(allItemVariations, itemVariation).length}`
      }
    })
    return itemVariationDescriptions
  }

  get discountsForOrder () {
    if (this.discountsForOrderMemo) return this.discountsForOrderMemo

    this.discountsForOrderMemo = _.map(this.discounts.data, discount => {
      const discountCopy = discount.clone()
      discountCopy.orderItemPackageOnly = true
      return discountCopy
    })

    return this.discountsForOrderMemo
  }

  get showAlcoholAcknowledgement () {
    if (!this.category) return false

    return this.category && this.category.hasAlcoholAcknowledgement && !this.ignoreAlcoholAcknowledgement
  }
}
